/**
 * 全局 H5 / 内部业务 API 加解密（与后端 HzHmacPayloadCryptoService 一致）
 * 配置：VUE_APP_API_CRYPTO_MODE / VUE_APP_API_CRYPTO_SECRET
 */
import CryptoJS from 'crypto-js'

const PAYLOAD_ENC = 'payloadEnc'

const DEFAULT_INCLUDE_PREFIXES = ['/hz/', '/business/', '/hz_rankings/', '/wx/rankings/']

const DEFAULT_EXCLUDE_PATHS = [
  '/hz/idm/oauth',
  '/hz/idm/admin',
  '/hz/idm/bim',
  '/hz/wx/oauth',
  '/hz/wx/user/login',
  '/hz/wx/user/loginByUserName',
  '/hz/wx/user/loginActivityDashboardOnly',
  '/hz/wx/zc',
  '/qb/unauth',
  '/business/idm/sync'
]

const FILE_TRANSFER_MARKERS = [
  '/export',
  '/common/download',
  '/upload',
  '/uploadimage',
  '/uploadcover',
  '/uploadavatar',
  '/importtemplate'
]

/** 统计/大屏等复杂数值结构，暂不走全局加解密（避免 BigDecimal 签名校验问题） */
const CRYPTO_SKIP_MARKERS = [
  '/statistics',
  '/userstats/',
  '/setstats/',
  '/online_activity_report/'
]

function isIncludedApiPath(path) {
  return DEFAULT_INCLUDE_PREFIXES.some((prefix) => path.startsWith(prefix) || path.includes(prefix))
}

function isFileTransferPath(path) {
  const lower = path.toLowerCase()
  return FILE_TRANSFER_MARKERS.some((marker) => lower.includes(marker))
}

function isCryptoSkippedPath(path) {
  const lower = path.toLowerCase()
  return CRYPTO_SKIP_MARKERS.some((marker) => lower.includes(marker))
}

/** 小数转 plain string，对齐 Java BigDecimal.stripTrailingZeros().toPlainString() */
function decimalToPlainString(n) {
  const s = String(n)
  if (!s.includes('.')) return s
  let out = s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '')
  if (out.endsWith('.')) out = out.slice(0, -1)
  return out
}

function canonicalLeaf(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (Number.isInteger(value)) return value
    return decimalToPlainString(value)
  }
  return value
}

export function isApiEncryptedMode() {
  const mode = (process.env.VUE_APP_API_CRYPTO_MODE || 'plain').trim()
  const secret = (process.env.VUE_APP_API_CRYPTO_SECRET || '').trim()
  return mode === 'encrypted' && secret.length > 0
}

function getSecret() {
  return (process.env.VUE_APP_API_CRYPTO_SECRET || '').trim()
}

function getMaxSkewMs() {
  const n = Number(process.env.VUE_APP_API_CRYPTO_MAX_SKEW_MS)
  return Number.isFinite(n) && n > 0 ? n : 60000
}

function normalizePath(url) {
  if (!url) return ''
  const raw = String(url).split('?')[0]
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    try {
      return new URL(raw).pathname
    } catch (e) {
      return raw
    }
  }
  return raw
}

export function shouldApiCrypto(url) {
  if (!isApiEncryptedMode()) return false
  const path = normalizePath(url)
  if (!isIncludedApiPath(path)) return false
  if (isFileTransferPath(path)) return false
  if (isCryptoSkippedPath(path)) return false
  return !DEFAULT_EXCLUDE_PATHS.some((item) => path === item || path.startsWith(item + '/') || path.endsWith(item))
}

function hmacSha256Hex(secret, message) {
  return CryptoJS.HmacSHA256(message, CryptoJS.enc.Utf8.parse(secret)).toString(CryptoJS.enc.Hex)
}

function base64Utf8Decode(b64) {
  const binary = atob(b64.trim())
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new TextDecoder('utf-8').decode(bytes)
}

function base64Utf8Encode(str) {
  const bytes = new TextEncoder().encode(str)
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return btoa(binary)
}

function sortObjectDeep(obj) {
  if (obj === null || typeof obj !== 'object') {
    return canonicalLeaf(obj)
  }
  if (Array.isArray(obj)) {
    return obj.map(sortObjectDeep)
  }
  const sorted = {}
  Object.keys(obj)
    .sort()
    .forEach((k) => {
      const v = obj[k]
      if (v !== undefined && v !== null) {
        sorted[k] = sortObjectDeep(v)
      }
    })
  return sorted
}

function canonicalJson(p) {
  return JSON.stringify(sortObjectDeep(p))
}

export function decodeApiPayloadEnc(payloadEnc) {
  const root = JSON.parse(base64Utf8Decode(payloadEnc))
  const { ts, p, sig } = root
  if (!p) {
    throw new Error('missing_p')
  }
  if (isApiEncryptedMode()) {
    if (ts == null || typeof ts !== 'number') {
      throw new Error('missing_ts')
    }
    if (!sig) {
      throw new Error('missing_sig')
    }
    if (Math.abs(Date.now() - ts) > getMaxSkewMs()) {
      throw new Error('ts_skew')
    }
    const expected = hmacSha256Hex(getSecret(), `${ts}|${canonicalJson(p)}`)
    if (expected.toLowerCase() !== String(sig).trim().toLowerCase()) {
      throw new Error('sig_mismatch')
    }
  }
  return p
}

export function encodeApiPayloadEnc(payload) {
  const secret = getSecret()
  const ts = Date.now()
  const sorted = sortObjectDeep(payload)
  const canonicalP = JSON.stringify(sorted)
  const sig = hmacSha256Hex(secret, `${ts}|${canonicalP}`)
  const envelope = { ts, p: JSON.parse(canonicalP), sig }
  return base64Utf8Encode(JSON.stringify(envelope))
}

export function wrapApiRequestBody(url, data, method) {
  if (!shouldApiCrypto(url)) return data
  const m = (method || 'get').toLowerCase()
  if (!['post', 'put', 'patch'].includes(m)) return data
  if (data == null) return data
  if (typeof data === 'string' || data instanceof FormData || data instanceof Blob) return data
  if (data[PAYLOAD_ENC]) return data
  return { [PAYLOAD_ENC]: encodeApiPayloadEnc(data) }
}

/** GET / POST 等 URL query 参数：合并为单个 payloadEnc */
export function wrapApiRequestParams(url, params, method) {
  if (!shouldApiCrypto(url)) return params
  if (params == null) return params
  if (params[PAYLOAD_ENC]) return params
  const keys = Object.keys(params).filter((k) => params[k] !== undefined && params[k] !== null)
  if (!keys.length) return params
  const picked = {}
  keys.forEach((k) => {
    picked[k] = params[k]
  })
  return { [PAYLOAD_ENC]: encodeApiPayloadEnc(picked) }
}

export function unwrapApiResponse(url, res) {
  if (!res || !shouldApiCrypto(url)) return res
  if (res.code !== 200) return res

  if (res.payloadEnc) {
    try {
      const p = decodeApiPayloadEnc(res.payloadEnc)
      const next = { ...res }
      delete next.payloadEnc
      if (p && typeof p === 'object') {
        if (p.rows !== undefined) next.rows = p.rows
        if (p.total !== undefined) next.total = p.total
      }
      return next
    } catch (e) {
      console.error('[API_CRYPTO] 列表解密失败', e)
      return { ...res, code: 500, msg: '数据解密失败', rows: [], total: 0 }
    }
  }

  if (!res.data) return res
  const enc = res.data[PAYLOAD_ENC]
  if (!enc) return res
  try {
    return { ...res, data: decodeApiPayloadEnc(enc) }
  } catch (e) {
    console.error('[API_CRYPTO] 解密失败', e)
    return { ...res, code: 500, msg: '数据解密失败', data: null }
  }
}
